import { Shield, Lock, Eye, Database, Globe, Users, FileText, Mail } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

export default function PrivacyPolicy() {
  const lastUpdated = "February 12, 2025";

  const sections = [
    {
      id: "introduction",
      title: "1. Introduction",
      content: `PrivacyShield ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services at privacyshield.store (the "Service").

We take your privacy seriously - after all, helping you protect your digital privacy is our core mission. Please read this policy carefully. By using our Service, you agree to the collection and use of information in accordance with this policy.`
    },
    {
      id: "information-collected",
      title: "2. Information We Collect",
      content: `We collect information that you provide directly to us and information collected automatically when you use our Service.

**Information You Provide:**
• Account Information: Name, email address, and password when you create an account
• Payment Information: Billing details processed securely through Stripe (we never store your full credit card number)
• Identity Protection Data: Email addresses you submit for breach checking (passwords are hashed locally and never stored)
• Communications: Messages you send to our support team or AI assistant

**Information Collected Automatically:**
• Device Information: Browser type, operating system, and device identifiers
• Usage Data: Pages visited, features used, and interaction patterns
• Log Data: IP address, access times, and referring URLs
• Cookies: Session and preference cookies (see Section 7)

**Information We Do NOT Collect:**
• Social Security Numbers
• Government-issued ID numbers
• Biometric data
• Precise geolocation data`
    },
    {
      id: "how-we-use",
      title: "3. How We Use Your Information",
      content: `We use the information we collect for the following purposes:

**Service Delivery:**
• Provide, maintain, and improve our privacy protection services
• Process breach checks against the Have I Been Pwned database
• Generate data removal requests on your behalf
• Deliver AI-powered privacy assistance

**Account Management:**
• Create and manage your account
• Process payments and subscriptions
• Send transactional emails (receipts, security alerts)

**Communication:**
• Respond to your inquiries and support requests
• Send breach alerts and security notifications (if enabled)
• Provide updates about our Service (you can opt out)

**Security & Compliance:**
• Detect and prevent fraud or abuse
• Comply with legal obligations
• Enforce our Terms of Service

**Improvement:**
• Analyze usage patterns to improve our Service
• Develop new features and functionality
• Conduct research and analytics (aggregated, anonymized)`
    },
    {
      id: "data-sharing",
      title: "4. How We Share Your Information",
      content: `**We do NOT sell your personal information. Ever.**

We may share your information only in the following limited circumstances:

**Service Providers:**
• Payment processors (Stripe) to handle transactions
• Cloud infrastructure providers to host our Service
• Analytics providers (aggregated data only)

**Legal Requirements:**
• To comply with applicable laws or legal processes
• To respond to lawful requests from government authorities
• To protect our rights, privacy, safety, or property

**Business Transfers:**
• In connection with a merger, acquisition, or sale of assets (you will be notified)

**With Your Consent:**
• When you explicitly authorize us to share information

**Data Broker Communications:**
When you use our data removal service, we send removal requests to data brokers on your behalf. These requests contain only the information necessary to identify and remove your records.`
    },
    {
      id: "data-security",
      title: "5. Data Security",
      content: `We implement industry-standard security measures to protect your information:

**Technical Safeguards:**
• Encryption in transit (TLS 1.3) and at rest (AES-256)
• Secure password hashing (bcrypt)
• Regular security audits and vulnerability assessments
• Automated threat detection and monitoring

**Operational Safeguards:**
• Limited employee access to personal data
• Security awareness training for all team members
• Incident response procedures

**Password Checking Security:**
When you use our password breach checker, your password is never sent to our servers or any third party in plain text. We use a k-Anonymity model where only the first 5 characters of your password's SHA-1 hash are transmitted.

While we strive to protect your information, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security but are committed to maintaining the highest standards.`
    },
    {
      id: "data-retention",
      title: "6. Data Retention",
      content: `We retain your information only as long as necessary for the purposes outlined in this policy:

**Account Data:** Retained while your account is active. Deleted within 30 days of account closure upon request.

**Breach Check Results:** Cached for 24 hours to improve performance, then automatically deleted.

**Payment Records:** Retained for 7 years as required by tax and financial regulations.

**Support Communications:** Retained for 2 years for quality assurance and dispute resolution.

**Aggregated Analytics:** May be retained indefinitely as this data is not personally identifiable.

You can request deletion of your data at any time by contacting us at privacy@privacyshield.store or through your account settings.`
    },
    {
      id: "cookies",
      title: "7. Cookies & Tracking",
      content: `We use cookies and similar technologies to enhance your experience:

**Essential Cookies:** Required for the Service to function (authentication, security)

**Preference Cookies:** Remember your settings and preferences

**Analytics Cookies:** Help us understand how users interact with our Service (aggregated)

**We do NOT use:**
• Advertising or tracking cookies
• Third-party marketing pixels
• Cross-site tracking technologies

You can control cookies through your browser settings. Disabling essential cookies may affect Service functionality.`
    },
    {
      id: "your-rights",
      title: "8. Your Privacy Rights",
      content: `Depending on your location, you may have the following rights:

**All Users:**
• Access your personal data
• Correct inaccurate data
• Delete your account and associated data
• Export your data in a portable format
• Opt out of marketing communications

**California Residents (CCPA):**
• Right to know what personal information is collected
• Right to know if personal information is sold or disclosed
• Right to say no to the sale of personal information (we don't sell data)
• Right to equal service and price (no discrimination for exercising rights)

**European/UK Residents (GDPR):**
• Right to access, rectification, and erasure
• Right to restrict or object to processing
• Right to data portability
• Right to withdraw consent
• Right to lodge a complaint with a supervisory authority

**To Exercise Your Rights:**
Contact us at privacy@privacyshield.store or use the settings in your account dashboard. We will respond within 30 days (or sooner as required by applicable law).`
    },
    {
      id: "children",
      title: "9. Children's Privacy",
      content: `Our Service is not intended for children under 16 years of age. We do not knowingly collect personal information from children under 16. If you are a parent or guardian and believe your child has provided us with personal information, please contact us at privacy@privacyshield.store and we will delete such information.`
    },
    {
      id: "international",
      title: "10. International Data Transfers",
      content: `Our Service is operated in the United States. If you access our Service from outside the United States, your information may be transferred to, stored, and processed in the United States or other countries where our service providers are located.

We ensure appropriate safeguards are in place for international transfers, including Standard Contractual Clauses for transfers from the EU/UK.`
    },
    {
      id: "changes",
      title: "11. Changes to This Policy",
      content: `We may update this Privacy Policy from time to time. We will notify you of material changes by:

• Posting the updated policy on our website
• Updating the "Last Updated" date
• Sending an email notification for significant changes

We encourage you to review this policy periodically. Your continued use of the Service after changes constitutes acceptance of the updated policy.`
    },
    {
      id: "contact",
      title: "12. Contact Us",
      content: `If you have questions about this Privacy Policy or our privacy practices, please contact us:

**Email:** privacy@privacyshield.store
**AI Assistant:** Chat with Alek at privacyshield.store/contact
**Mail:** PrivacyShield, [Your Business Address]

For GDPR-related inquiries, you may also contact our Data Protection contact at dpo@privacyshield.store.`
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505] py-8 md:py-16" data-testid="privacy-policy-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#00FF94]/10 border border-[#00FF94]/20 mb-4">
            <Lock className="w-8 h-8 text-[#00FF94]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Privacy Policy
          </h1>
          <p className="text-gray-400">
            Last updated: {lastUpdated}
          </p>
        </div>

        {/* Quick Summary */}
        <Card className="bg-[#00FF94]/5 border-[#00FF94]/20 mb-8">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-[#00FF94] mb-3">Quick Summary</h2>
            <ul className="space-y-2 text-sm text-gray-300">
              <li className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#00FF94]" />
                We never sell your personal data
              </li>
              <li className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#00FF94]" />
                Your data is encrypted at rest and in transit
              </li>
              <li className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-[#00FF94]" />
                We don't use tracking or advertising cookies
              </li>
              <li className="flex items-center gap-2">
                <Database className="w-4 h-4 text-[#00FF94]" />
                You can delete your data at any time
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Table of Contents */}
        <Card className="bg-[#0a0a0a] border-[#262626] mb-8">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Table of Contents</h2>
            <nav className="grid md:grid-cols-2 gap-2">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="text-sm text-gray-400 hover:text-[#00FF94] transition-colors"
                >
                  {section.title}
                </a>
              ))}
            </nav>
          </CardContent>
        </Card>

        {/* Policy Sections */}
        <div className="space-y-8">
          {sections.map((section) => (
            <section key={section.id} id={section.id} className="scroll-mt-8">
              <Card className="bg-[#0a0a0a] border-[#262626]">
                <CardContent className="p-6">
                  <h2 className="text-xl font-semibold text-white mb-4">{section.title}</h2>
                  <div className="text-gray-400 text-sm leading-relaxed whitespace-pre-wrap prose prose-invert prose-sm max-w-none">
                    {section.content.split('**').map((part, i) => 
                      i % 2 === 1 ? <strong key={i} className="text-white">{part}</strong> : part
                    )}
                  </div>
                </CardContent>
              </Card>
            </section>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500">
            Questions about our privacy practices?{' '}
            <a href="/contact" className="text-[#00FF94] hover:underline">
              Contact Alek
            </a>
          </p>
        </div>

      </div>
    </div>
  );
}
